import React from "react";
import { motion } from "framer-motion";

/**
 * AdWup Mascot — the house-bot.
 * Brand variants:
 *  - "purple-kawaii": default — visible on any light background (white, gray, light-purple)
 *  - "white-kawaii":  ONLY for dark backgrounds (dark hero sections, modals on dark overlay)
 *  - "worried":       error/cancellation/payment failed states
 *  - "green":         growth / icon mark / win states (icon-only, no face)
 *  - "purple":        alt branding / variety (icon-only, no face)
 *
 * Animations (CSS-driven, no Motion deps unless needed):
 *  - "float":  gentle up/down loop (hero, greeting)
 *  - "bounce": one-shot bounce on mount (success)
 *  - "wave":   rotates slightly side-to-side (onboarding)
 *  - "pulse":  subtle scale loop (loading)
 *  - false:    static
 */

const ASSETS = {
  "purple-kawaii": "/images/mascots/housebot-purple.png",
  "white-kawaii": "/images/mascots/housebot-white.png",
  worried: "/images/mascots/housebot-worried.png",
  green: "/images/mascots/icon-green.png",
  purple: "/images/mascots/icon-purple.png",
};

const ANIM_PROPS = {
  float: {
    animate: { y: [0, -8, 0] },
    transition: { duration: 3, repeat: Infinity, ease: "easeInOut" },
  },
  bounce: {
    initial: { scale: 0, rotate: -10 },
    animate: { scale: 1, rotate: 0 },
    transition: { type: "spring", stiffness: 260, damping: 14 },
  },
  wave: {
    animate: { rotate: [0, -6, 6, -6, 0] },
    transition: { duration: 2.4, repeat: Infinity, repeatDelay: 1.2, ease: "easeInOut" },
  },
  pulse: {
    animate: { scale: [1, 1.06, 1] },
    transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
  },
};

export default function Mascot({
  variant = "purple-kawaii",
  size = 96,
  animation = false,
  className = "",
  alt = "AdWup mascot",
  priority = false,
  ...rest
}) {
  const src = ASSETS[variant] || ASSETS["purple-kawaii"];
  const animProps = animation && ANIM_PROPS[animation] ? ANIM_PROPS[animation] : null;

  const img = (
    <img
      src={src}
      alt={alt}
      width={size}
      height={size}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      className={`select-none pointer-events-none ${className}`}
      draggable={false}
      style={{ width: size, height: size }}
      data-testid={`mascot-${variant}`}
      {...rest}
    />
  );

  if (!animProps) return img;

  return (
    <motion.div
      {...animProps}
      style={{ display: "inline-block", lineHeight: 0 }}
      data-testid={`mascot-wrap-${variant}`}
    >
      {img}
    </motion.div>
  );
}
